import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { createSupportTicket } from '../services/paymentService';
import { APCGSupportTicketInsert } from '../types/payments';

interface SupportTicketModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const SupportTicketModal: React.FC<SupportTicketModalProps> = ({
  isOpen,
  onClose
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<APCGSupportTicketInsert>({
    FULL_NAME: '',
    MOBILE_NUMBER: '',
    EMAIL: '',
    ISSUE: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [ticketId, setTicketId] = useState<string>('');
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    if (isOpen) {
      // Reset form when modal opens
      setFormData({
        FULL_NAME: '',
        MOBILE_NUMBER: '',
        EMAIL: '',
        ISSUE: ''
      });
      setErrors({});
      setIsSuccess(false);
      setTicketId('');
    }
  }, [isOpen]);

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.FULL_NAME.trim()) {
      newErrors.FULL_NAME = t('paymentPage.required');
    }

    if (!formData.EMAIL.trim()) {
      newErrors.EMAIL = t('paymentPage.required');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.EMAIL)) {
      newErrors.EMAIL = t('paymentPage.invalidEmail');
    }

    if (!formData.MOBILE_NUMBER.trim()) {
      newErrors.MOBILE_NUMBER = t('paymentPage.required');
    } else if (!/^[\+]?[1-9][\d]{0,15}$/.test(formData.MOBILE_NUMBER.replace(/\s/g, ''))) {
      newErrors.MOBILE_NUMBER = t('paymentPage.invalidPhone');
    }

    if (!formData.ISSUE.trim()) {
      newErrors.ISSUE = t('paymentPage.required');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await createSupportTicket(formData);
      setTicketId(response.ticketId);
      setIsSuccess(true);
    } catch (error) {
      console.error('Support ticket submission failed:', error);
      alert(t('paymentPage.ticketSubmissionFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(5px)' }}>
      <div className="modal-dialog modal-dialog-centered modal-md modal-dialog-scrollable" style={{ maxWidth: '600px', margin: '1rem auto' }}>
        <div className="modal-content" style={{ 
          borderRadius: '15px', 
          border: 'none', 
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          overflow: 'hidden'
        }}>
          <div className="modal-header" style={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            border: 'none',
            padding: '1rem 1.25rem'
          }}>
            <div className="d-flex align-items-center justify-content-between w-100">
              <div className="d-flex align-items-center">
                <i className="bi bi-headset me-2" style={{ fontSize: '1.25rem' }}></i>
                <h6 className="modal-title fw-700 mb-0 text-nowrap">
                  {t('paymentPage.supportTicketTitle')}
                </h6>
              </div>
              <button
                type="button"
                className="btn-close btn-close-white flex-shrink-0"
                onClick={handleClose}
                disabled={isSubmitting}
                style={{ filter: 'invert(1)' }}
              ></button>
            </div>
          </div>
          
          <div className="modal-body" style={{ padding: '1.25rem' }}>
            {!isSuccess ? (
              <>
                <div className="text-center mb-3">
                  <div className="bg-primary bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-2" 
                       style={{ width: '60px', height: '60px' }}>
                    <i className="bi bi-question-circle text-primary" style={{ fontSize: '1.5rem' }}></i>
                  </div>
                  <p className="text-dark mb-0 fw-500" style={{ fontSize: '0.95rem' }}>
                    {t('paymentPage.supportTicketDescription')}
                  </p>
                </div>
                
                <form onSubmit={handleSubmit}>
                  <div className="row g-2">
                    <div className="col-12 col-md-6 mb-2">
                      <label className="form-label fw-600 text-dark mb-1" style={{ fontSize: '0.9rem' }}>
                        {t('paymentPage.fullName')} <span className="text-danger">*</span>
                      </label>
                      <div className="input-group">
                        <span className="input-group-text bg-light border-end-0" style={{ borderColor: '#dee2e6' }}>
                          <i className="bi bi-person text-muted"></i>
                        </span>
                        <input
                          type="text"
                          className={`form-control border-start-0 ${errors.FULL_NAME ? 'is-invalid' : ''}`}
                          name="FULL_NAME"
                          value={formData.FULL_NAME}
                          onChange={handleChange}
                          disabled={isSubmitting}
                          placeholder="Enter your full name"
                          style={{ borderColor: '#dee2e6' }}
                        />
                      </div>
                      {errors.FULL_NAME && (
                        <div className="invalid-feedback d-block">{errors.FULL_NAME}</div>
                      )}
                    </div>
                    
                    <div className="col-12 col-md-6 mb-2">
                      <label className="form-label fw-600 text-dark mb-1" style={{ fontSize: '0.9rem' }}>
                        {t('paymentPage.mobileNumber')} <span className="text-danger">*</span>
                      </label>
                      <div className="input-group">
                        <span className="input-group-text bg-light border-end-0" style={{ borderColor: '#dee2e6' }}>
                          <i className="bi bi-phone text-muted"></i>
                        </span>
                        <input
                          type="tel"
                          className={`form-control border-start-0 ${errors.MOBILE_NUMBER ? 'is-invalid' : ''}`}
                          name="MOBILE_NUMBER"
                          value={formData.MOBILE_NUMBER}
                          onChange={handleChange}
                          disabled={isSubmitting}
                          placeholder="+966501234567"
                          style={{ borderColor: '#dee2e6' }}
                        />
                      </div>
                      {errors.MOBILE_NUMBER && (
                        <div className="invalid-feedback d-block">{errors.MOBILE_NUMBER}</div>
                      )}
                    </div>
                  </div>
                  
                  <div className="mb-2">
                    <label className="form-label fw-600 text-dark mb-1" style={{ fontSize: '0.9rem' }}>
                      {t('paymentPage.email')} <span className="text-danger">*</span>
                    </label>
                    <div className="input-group">
                      <span className="input-group-text bg-light border-end-0" style={{ borderColor: '#dee2e6' }}>
                        <i className="bi bi-envelope text-muted"></i>
                      </span>
                      <input
                        type="email"
                        className={`form-control border-start-0 ${errors.EMAIL ? 'is-invalid' : ''}`}
                        name="EMAIL"
                        value={formData.EMAIL}
                        onChange={handleChange}
                        disabled={isSubmitting}
                        placeholder="Enter your email address"
                        style={{ borderColor: '#dee2e6' }}
                      />
                    </div>
                    {errors.EMAIL && (
                      <div className="invalid-feedback d-block">{errors.EMAIL}</div>
                    )}
                  </div>
                  
                  <div className="mb-2">
                    <label className="form-label fw-600 text-dark mb-1" style={{ fontSize: '0.9rem' }}>
                      {t('paymentPage.issueDescription')} <span className="text-danger">*</span>
                    </label>
                    <div className="input-group">
                      <span className="input-group-text bg-light border-end-0 align-items-start pt-3" style={{ borderColor: '#dee2e6' }}>
                        <i className="bi bi-chat-dots text-muted"></i>
                      </span>
                      <textarea
                        className={`form-control border-start-0 ${errors.ISSUE ? 'is-invalid' : ''}`}
                        name="ISSUE"
                        value={formData.ISSUE}
                        onChange={handleChange}
                        rows={3}
                        disabled={isSubmitting}
                        placeholder="Please describe your issue in detail..."
                        style={{ resize: 'none', borderColor: '#dee2e6' }}
                      />
                    </div>
                    {errors.ISSUE && (
                      <div className="invalid-feedback d-block">{errors.ISSUE}</div>
                    )}
                  </div>
                  
                  <div className="d-flex flex-column flex-sm-row justify-content-end gap-2 mt-3">
                    <button
                      type="button"
                      className="btn btn-outline-danger px-3 py-2 fw-600"
                      onClick={handleClose}
                      disabled={isSubmitting}
                      style={{ 
                        transition: 'all 0.3s ease',
                        borderWidth: '2px',
                        color: '#dc3545',
                        borderColor: '#dc3545',
                        borderRadius: '8px',
                        fontSize: '13px',
                        letterSpacing: '0.5px',
                        textTransform: 'uppercase'
                      }}
                      onMouseEnter={(e) => {
                        if (!isSubmitting) {
                          e.currentTarget.style.backgroundColor = '#dc3545';
                          e.currentTarget.style.color = 'white';
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(220, 53, 69, 0.3)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isSubmitting) {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.color = '#dc3545';
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = 'none';
                        }
                      }}
                    >
                      <i className="bi bi-x-circle me-2"></i>
{t('paymentPage.close')}
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary px-3 py-2 fw-600"
                      disabled={isSubmitting}
                      style={{ 
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        border: 'none',
                        transition: 'all 0.3s ease',
                        boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                        borderRadius: '8px',
                        fontSize: '13px',
                        letterSpacing: '0.5px',
                        textTransform: 'uppercase'
                      }}
                      onMouseEnter={(e) => {
                        if (!isSubmitting) {
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.6)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isSubmitting) {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '0 4px 15px rgba(102, 126, 234, 0.4)';
                        }
                      }}
                    >
                      {isSubmitting ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
{t('paymentPage.submitTicket')}...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-send me-2"></i>
{t('paymentPage.submitTicket')}
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </>
            ) : (
              <div className="text-center py-4">
                <div className="mb-4">
                  <div className="bg-success bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-3" 
                       style={{ width: '100px', height: '100px' }}>
                    <i className="bi bi-check-circle-fill text-success" style={{ fontSize: '3rem' }}></i>
                  </div>
                </div>
                <h4 className="text-success mb-3 fw-700">{t('paymentPage.ticketSubmitted')}</h4>
                <div className="bg-light rounded-3 p-4 mb-4">
                  <p className="text-dark mb-2 fw-600">
                    <i className="bi bi-ticket-perforated me-2 text-primary"></i>
                    {t('paymentPage.ticketId')}:
                  </p>
                  <p className="text-primary fs-4 fw-bold mb-0 font-monospace">{ticketId}</p>
                </div>
                <p className="text-muted mb-3" style={{ fontSize: '0.95rem' }}>
                  <i className="bi bi-clock me-2"></i>
                  {t('paymentPage.supportTeamWillContact')}
                </p>
                <button
                  type="button"
                  className="btn btn-outline-primary px-3 py-2 fw-600"
                  onClick={handleClose}
                  style={{ 
                    transition: 'all 0.3s ease',
                    borderWidth: '2px',
                    color: '#007bff',
                    borderColor: '#007bff',
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    fontSize: '14px',
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#007bff';
                    e.currentTarget.style.color = 'white';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 123, 255, 0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'white';
                    e.currentTarget.style.color = '#007bff';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <i className="bi bi-check-circle me-2"></i>
{t('paymentPage.close')}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupportTicketModal;
